// setTableTablespace — DdlEdit builder + commit dispatcher for the
// tablespace / filegroup creation flow surfaced from `StorageTab` and the
// inline DDL toolbar (DSE2 §3.E2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice E2 — Tablespaces / filegroups UI.
//
// The Storage tab's "Tablespace / Filegroup" affordance opens a popover that
// declares a fresh storage container the user can then reference from
// CREATE TABLE / CREATE INDEX. The popover dispatches DSE2's existing
// `create-tablespace` (pgsql / oracle) or `create-filegroup` (mssql) op.
//
// Per-engine surface:
//   - pgsql:  CREATE TABLESPACE name [OWNER role] LOCATION 'dir'
//   - oracle: CREATE [BIGFILE|SMALLFILE] TABLESPACE name DATAFILE 'file'
//             [SIZE n]
//   - mssql:  ALTER DATABASE <db> ADD FILEGROUP <name>
//   - mysql:  surface disabled — MySQL general tablespaces are an advanced
//             InnoDB-only knob; managing them from this editor entry is out
//             of scope for the slice. (DSE2's `create-tablespace` does
//             accept mysql; consumers can opt in via a future slice.)
//   - sqlite: surface disabled — no tablespace concept.
//
// The consumer uses a single discriminated `kind` arm so callers don't have
// to think about the cross-engine TablespaceDef / FilegroupDef split — the
// builder picks the right DSE2 op based on the discriminator.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  FilegroupDef,
  TablespaceDef,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type SetTableTablespaceArgs =
  | { readonly kind: 'tablespace'; readonly def: TablespaceDef }
  | { readonly kind: 'filegroup'; readonly def: FilegroupDef };

export function buildSetTableTablespaceEdit(args: SetTableTablespaceArgs): DdlEdit {
  if (args.kind === 'tablespace') {
    return { op: 'create-tablespace', tablespace: args.def };
  }
  return { op: 'create-filegroup', filegroup: args.def };
}

export async function commitSetTableTablespace(
  ctx: VqeDdlEditContext,
  args: SetTableTablespaceArgs,
): Promise<void> {
  const edit = buildSetTableTablespaceEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const SET_TABLESPACE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'oracle',
] as const);

export function isSetTableTablespaceSupported(engine: SqlEngine): boolean {
  return SET_TABLESPACE_SUPPORTED.has(engine);
}

export function getSetTableTablespaceUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mysql') {
    return (
      'MySQL general tablespaces are an advanced InnoDB-only feature; this '
      + 'editor surfaces them only through raw SQL. Use the SQL editor to '
      + 'issue CREATE TABLESPACE … ADD DATAFILE … ENGINE = InnoDB directly.'
    );
  }
  if (engine === 'sqlite') {
    return (
      'SQLite has no tablespace concept — every database is a single file. '
      + 'Use ATTACH DATABASE to span multiple files.'
    );
  }
  return '';
}

/** The storage-container kind the engine declares. mssql ships filegroups
 *  (database-scoped); pgsql / oracle ship tablespaces (server-scoped). */
export type SetTableTablespaceKind = SetTableTablespaceArgs['kind'];

export function getSetTableTablespaceKind(engine: SqlEngine): SetTableTablespaceKind | null {
  if (engine === 'mssql') return 'filegroup';
  if (engine === 'postgres' || engine === 'oracle') return 'tablespace';
  return null;
}

/** Pre-flight validator. Returns null when args are acceptable, else a
 *  user-facing message. */
export function validateSetTableTablespaceForEngine(
  engine: SqlEngine,
  args: SetTableTablespaceArgs,
): string | null {
  if (!isSetTableTablespaceSupported(engine)) {
    return getSetTableTablespaceUnsupportedTooltip(engine);
  }
  const expectedKind = getSetTableTablespaceKind(engine);
  if (expectedKind !== null && args.kind !== expectedKind) {
    return (
      `${engine} declares storage containers as `
      + `${expectedKind === 'filegroup' ? 'filegroups' : 'tablespaces'}, `
      + `not ${args.kind === 'filegroup' ? 'filegroups' : 'tablespaces'}.`
    );
  }
  if (args.kind === 'tablespace') {
    const def = args.def;
    if (def.name.trim().length === 0) {
      return 'Tablespace name is required.';
    }
    if (def.location.trim().length === 0) {
      return engine === 'oracle'
        ? 'Oracle tablespaces require a DATAFILE path.'
        : 'PostgreSQL tablespaces require a LOCATION directory.';
    }
    return null;
  }
  // filegroup
  const def = args.def;
  if (def.name.trim().length === 0) {
    return 'Filegroup name is required.';
  }
  if (def.database.trim().length === 0) {
    return 'Filegroup must be scoped to a target database.';
  }
  return null;
}
